// services/notificationService.ts

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from "axios";
import { BACKEND_URL } from "../../config/api";

class NotificationService {
  private token: string | null = null;
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: BACKEND_URL,
    });

    // Intercepteur pour injecter automatiquement le token
    this.client.interceptors.request.use((config) => {
      if (this.token) {
        config.headers = config.headers ?? {};
        config.headers["Authorization"] = `Bearer ${this.token}`;
      }
      return config;
    });
  }

  setToken(token: string) {
    this.token = token;
  }

  /**
   * Extrait un message d'erreur lisible depuis une AxiosError.
   */
  private extractErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<any>;
      const data = axiosError.response?.data;
      if (data && typeof data === "object") {
        return data.message || data.detail || axiosError.message || "Erreur serveur";
      }
      return axiosError.message || "Erreur serveur";
    }
    if (error instanceof Error) return error.message;
    return "Erreur inconnue";
  }

  /**
   * Requête générique JSON via Axios.
   */
  private async request<T = any>(endpoint: string, options: AxiosRequestConfig = {}): Promise<T> {
    try {
      const response = await this.client.request<T>({
        url: endpoint,
        ...options,
      });
      return response.data;
    } catch (error) {
      throw new Error(this.extractErrorMessage(error));
    }
  }

  // --- NOTIFICATIONS IN-APP ---
  async sendNotification(payload: {
    title: string;
    message: string;
    type: "TRANSACTION" | "SYSTEM" | "ADMIN" | "KYC" | "PROMOTION" | "SECURITY";
    priority: "LOW" | "NORMAL" | "HIGH" | "URGENT";
    userId?: string;
    data?: string;
    actionUrl?: string;
    actionLabel?: string;
  }) {
    const isBroadcast = !payload.userId;
    const endpoint = isBroadcast ? "/notifications/admin/broadcast" : "/notifications/admin/create";

    const body: Record<string, any> = {
      title: payload.title,
      message: payload.message,
      type: payload.type,
      priority: payload.priority,
    };

    if (payload.userId) body.user_id = payload.userId;
    if (payload.data) body.data = payload.data;
    if (payload.actionUrl) body.action_url = payload.actionUrl;
    if (payload.actionLabel) body.action_label = payload.actionLabel;

    return this.request(endpoint, {
      method: "POST",
      data: body,
    });
  }

  // --- PUSH NOTIFICATIONS ---
  async sendPushNotification(payload: {
    title: string;
    description: string;
    userIds?: string[];
    broadcast?: boolean;
    image?: string;
  }) {
    const body: Record<string, any> = {
      title: payload.title,
      description: payload.description,
    };

    if (payload.broadcast) {
      body.broadcast = true;
    } else if (payload.userIds) {
      body.user_ids = payload.userIds;
    }

    if (payload.image) body.image = payload.image;

    return this.request("/notifications/admin/push", {
      method: "POST",
      data: body,
    });
  }

  // --- EMAIL SANS TEMPLATE ---
  async sendEmail(payload: {
    subject: string;
    message: string;
    userIds?: string[];
    broadcast?: boolean;
    attachments?: File[];
  }) {
    const formData = new FormData();
    formData.append("subject", payload.subject);
    formData.append("message", payload.message);

    if (payload.broadcast) {
      formData.append("broadcast", "true");
    } else if (payload.userIds) {
      payload.userIds.forEach((id) => formData.append("user_ids", id));
    }

    if (payload.attachments) {
      payload.attachments.forEach((file) => formData.append("attachments", file));
    }

    return this.request("/emails/admin/send", {
      method: "POST",
      data: formData,
    });
  }

  // --- EMAIL AVEC TEMPLATE (placeholders) ---
  async sendTemplateEmail(payload: {
    subject: string;
    html: string;
    userIds?: string[];
    broadcast?: boolean;
    attachments?: File[];
  }) {
    const formData = new FormData();
    formData.append("subject", payload.subject);
    formData.append("html", payload.html);

    if (payload.broadcast) {
      formData.append("broadcast", "true");
    } else if (payload.userIds) {
      payload.userIds.forEach((id) => formData.append("user_ids", id));
    }

    if (payload.attachments) {
      payload.attachments.forEach((file) => formData.append("attachments", file));
    }

    try {
      const response = await this.client.post("/emails/admin/send-template", formData);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error(error.response);
        throw new Error(this.extractErrorMessage(error));
      }
      console.error(error);
      throw error;
    }
  }
  // --- GESTION DES DESINSCRITS ---
  async getUnsubscribedEmails() {
    return this.request("/emails/unsubscribed", {
      method: "GET",
    });
  }

  async addUnsubscribedEmail(email: string, userId?: string) {
    const body: Record<string, any> = { email };
    if (userId) body.user_id = userId;
    return this.request("/emails/unsubscribed", {
      method: "POST",
      data: body,
    });
  }

  async removeUnsubscribedEmail(unsubscribeId: string) {
    return this.request(`/emails/unsubscribed/${unsubscribeId}`, {
      method: "DELETE",
    });
  }

  // --- GESTION DES DESINSCRITS NOTIFICATIONS ---
  async getUnsubscribedNotifications() {
    return this.request("/notifications/unsubscribed", {
      method: "GET",
    });
  }

  async addUnsubscribedNotification(email: string, userId?: string) {
    const body: Record<string, any> = { email };
    if (userId) body.user_id = userId;
    return this.request("/notifications/unsubscribed", {
      method: "POST",
      data: body,
    });
  }

  async removeUnsubscribedNotification(unsubscribeId: string) {
    return this.request(`/notifications/unsubscribed/${unsubscribeId}`, {
      method: "DELETE",
    });
  }

  // --- DESINSCRIPTION PUBLIQUE (depuis le lien email) ---
  async publicUnsubscribe(email: string, userId?: string) {
    const body: Record<string, any> = { email };
    if (userId) body.user_id = userId;

    try {
      const response = await axios.post(`${BACKEND_URL}/emails/unsubscribe`, body, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      throw new Error(this.extractErrorMessage(error));
    }
  }
}

export const notificationService = new NotificationService();
